import React, { Fragment } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import Header from '../../components/Header'
import Load from '../../components/Load'
import StatusChip from '../../components/StatusChip'
import TeamResult from '../../components/TeamResult'
import ScoreBar from '../../components/ScoreBar'
import ScoreLine from '../../components/ScoreLine'
import {
  PARTIDA_POR_ID,
  PartidaPorIdData,
  PartidaPorIdVars,
} from '../../services/partidaService'
import { atletasDoLado, ladoVencedor } from '../../utils/atletasDoLado'

const SingleMatchPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data, loading, error } = useQuery<PartidaPorIdData, PartidaPorIdVars>(
    PARTIDA_POR_ID,
    { variables: { id: id || '' }, skip: !id }
  )

  const match = data?.partidaPorId

  const playersA = match
    ? atletasDoLado(match, 'A').map(a => ({ nome: a.nome, imagemUrl: a.imagemUrl }))
    : []

  const playersB = match
    ? atletasDoLado(match, 'B').map(a => ({ nome: a.nome, imagemUrl: a.imagemUrl }))
    : []

  // Vencedor pelo lado "A"/"B" (fonte única — cobre rodízio e legado).
  const winner: 'A' | 'B' | null = match ? ladoVencedor(match) : null
  const winnerScoreLine = (gamesA: number | null, gamesB: number | null) => {
    if (gamesA === null || gamesB === null) return null
    return (
      <ScoreLine
        scoreA={gamesA}
        scoreB={gamesB}
        winner={gamesA > gamesB ? 'A' : gamesB > gamesA ? 'B' : undefined}
      />
    )
  }

  return (
    <Fragment>
      <Header />
      <div className="main-content right-chat-active">
        <div className="container mt-4">
          <button className="btn btn-link p-0 mb-3" onClick={() => navigate(-1)}>
            <i className="bi bi-arrow-left" />
          </button>
          <h4 className="d-inline-block ms-2">Partida</h4>

          {loading && <Load />}
          {error && <p className="text-danger mt-3">Erro ao carregar partida.</p>}

          {!loading && !error && !match && (
            <p className="mt-3">Partida nao encontrada.</p>
          )}

          {!loading && match && (
            <div className="mt-3">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <TeamResult
                  players={playersA}
                  result={winner === 'A' ? 'win' : winner === 'B' ? 'loss' : undefined}
                />
                <div className="text-center">
                  <StatusChip status={match.status} />
                  {match.categoriaTorneio?.nome && (
                    <div className="small text-muted mt-1">
                      {match.categoriaTorneio?.nome}
                    </div>
                  )}
                </div>
                <TeamResult
                  players={playersB}
                  result={winner === 'B' ? 'win' : winner === 'A' ? 'loss' : undefined}
                />
              </div>
              <ScoreBar
                scoreA={match.placar?.resultadoFinalA ?? null}
                scoreB={match.placar?.resultadoFinalB ?? null}
                winner={winner}
              />
              {match.placar?.sets?.map(set => (
                <div key={set?.numeroSet} className="mt-1">
                  <div className="small text-center">Set {set?.numeroSet}</div>
                  {winnerScoreLine(set?.gamesA, set?.gamesB)}
                  {(set?.tiebreakA != null || set?.tiebreakB != null) && (
                    <div className="text-center small text-muted">
                      TB {set?.tiebreakA} x {set?.tiebreakB}
                    </div>
                  )}
                </div>
              ))}
              {match.local && (
                <div className="text-center mt-3">
                  <div>{match.local?.nome}</div>
                  <div className="small text-muted">
                    {match.local?.endereco?.logradouro}, {match.local?.endereco?.numero} -{' '}
                    {match.local?.endereco?.cidade?.nome}/{match.local?.endereco?.cidade?.estado?.uf}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Fragment>
  )
}

export default SingleMatchPage
